import { Navigate, Route, matchPath } from 'react-router-dom';
import admissionsComponents from '../../../../../config/semantic-components/admissions-intellio-v2.json';
import admissionsRoutesConfig from '../../../../../config/semantic-routes/admissions.json';
import {
  Dashboard as AdmissionsDashboardRoute,
} from '../../internal-owned/school-admissions-v2-1/pages/Dashboard';
import {
  ApplicantDetail as AdmissionsApplicantDetailRoute,
} from '../../internal-owned/school-admissions-v2-1/pages/ApplicantDetail';
import {
  ChildrenTab as AdmissionsChildrenTab,
} from '../../internal-owned/school-admissions-v2-1/pages/tabs/ChildrenTab';
import {
  FamilyTab as AdmissionsFamilyTab,
} from '../../internal-owned/school-admissions-v2-1/pages/tabs/FamilyTab';
import {
  DocumentsTab as AdmissionsDocumentsTab,
} from '../../internal-owned/school-admissions-v2-1/pages/tabs/DocumentsTab';
import {
  NotesTab as AdmissionsNotesTab,
} from '../../internal-owned/school-admissions-v2-1/pages/tabs/NotesTab';
import {
  TuitionTab as AdmissionsTuitionTab,
} from '../../internal-owned/school-admissions-v2-1/pages/tabs/TuitionTab';
import {
  SemanticRouteAdapter,
  type SemanticComponentsConfig,
  type SemanticRoutesConfig,
} from '../SemanticRouteAdapter';

const componentsConfig = admissionsComponents as SemanticComponentsConfig;
const routesConfig = admissionsRoutesConfig as SemanticRoutesConfig;

type DetailTab = 'family' | 'children' | 'documents' | 'notes' | 'tuition';

const DETAIL_TABS: DetailTab[] = ['family', 'children', 'documents', 'notes', 'tuition'];

const normalizeSubroute = (value?: string): string | null => {
  const normalized = value?.replace(/^\/+|\/+$/g, '') ?? '';
  return normalized.length > 0 ? normalized : null;
};

const normalizeDetailTab = (value?: string): DetailTab => {
  if (value && DETAIL_TABS.includes(value as DetailTab)) {
    return value as DetailTab;
  }
  return 'children';
};

const resolveAdmissionsDetailPath = (params: Record<string, string | undefined>): string | null => {
  const wildcard = normalizeSubroute(params['*']);
  if (wildcard) {
    const [tab, familyId] = wildcard.split('/');
    if (tab && familyId && DETAIL_TABS.includes(tab as DetailTab)) {
      return `/school/admissions/${tab}/${familyId}`;
    }
  }
  const familyId = params.familyId ?? params.applicationId ?? params.id;
  if (familyId) {
    return `/school/admissions/children/${familyId}`;
  }
  return null;
};

const resolveInternalAdmissionsPath = (
  componentId: string,
  params: Record<string, string | undefined>,
): string | null => {
  if (componentId === 'admissions-dashboard') {
    return '/school/admissions/dashboard';
  }
  if (componentId === 'admissions-applicant-detail') {
    return resolveAdmissionsDetailPath(params);
  }
  return null;
};

const mapCanonicalAdmissionsPath = (pathname: string | null): string | null => {
  if (!pathname) return pathname;
  const normalized = pathname.replace(/\/+$/, '') || '/';

  if (normalized === '/school/admissions') {
    return '/admin/admissions/staff/dashboard';
  }
  if (normalized === '/school/admissions/dashboard') {
    return '/admin/admissions/staff/dashboard';
  }

  const detailPatterns = [
    '/school/admissions/family/:familyId',
    '/school/admissions/children/:familyId',
    '/school/admissions/documents/:familyId',
    '/school/admissions/notes/:familyId',
    '/school/admissions/tuition/:familyId',
  ];

  for (const pattern of detailPatterns) {
    const detail = matchPath({ path: pattern, end: true }, normalized);
    if (detail?.params.familyId) {
      return `/admin/admissions/staff/applicant/${detail.params.familyId}`;
    }
  }

  return pathname;
};

const AdmissionsInternalRoutes = (
  <>
    <Route index element={<Navigate to="dashboard" replace />} />
    <Route path="dashboard" element={<AdmissionsDashboardRoute />} />
    <Route path="children/:id" element={<AdmissionsApplicantDetailRoute />}>
      <Route index element={<AdmissionsChildrenTab />} />
    </Route>
    <Route path="family/:id" element={<AdmissionsApplicantDetailRoute />}>
      <Route index element={<AdmissionsFamilyTab />} />
    </Route>
    <Route path="documents/:id" element={<AdmissionsApplicantDetailRoute />}>
      <Route index element={<AdmissionsDocumentsTab />} />
    </Route>
    <Route path="notes/:id" element={<AdmissionsApplicantDetailRoute />}>
      <Route index element={<AdmissionsNotesTab />} />
    </Route>
    <Route path="tuition/:id" element={<AdmissionsApplicantDetailRoute />}>
      <Route index element={<AdmissionsTuitionTab />} />
    </Route>
    <Route path="*" element={<Navigate to="dashboard" replace />} />
  </>
);

export function AdmissionsSemanticRoute() {
  return (
    <section data-extracted-from="owned:ystnj/semantic/admissions-route">
      <SemanticRouteAdapter
        moduleKey="admissions"
        componentsConfig={componentsConfig}
        routesConfig={routesConfig}
        mapIntegrationPath={mapCanonicalAdmissionsPath}
        strictNoFallback
        blockBridgedTarget
        internal={{
          resolvePath: resolveInternalAdmissionsPath,
          routes: AdmissionsInternalRoutes,
        }}
      />
    </section>
  );
}
